import { AppStateEnum } from '../../../../../main/types/app-state.types.ts';
import { StepType } from '../../../../../main/types/asset/asset.enums.ts';

import { SoundsWritingErrors } from '../../../../constants/errors.ts';
import { SOUNDS_WRITING_RECOGNITION_MESSAGES } from '../../../../constants/sounds-writing.ts';
import { selectCurrentLessonTaskStep } from '../../../../selectors/current-lesson-task-step.selector.ts';
import { updateAppStateThunk } from '../../../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../../../slices/ball/ball.slice.ts';
import { setScriptReplacement } from '../../../../slices/scripts/scripts.slice.ts';
import { ScriptReplacementKey } from '../../../../slices/scripts/scripts.slice.types.ts';
import { disableDrawing, enableDrawing } from '../../../../slices/sounds-writing/sounds-writing.slice.ts';
import { CheckWritingEvaluation, CheckWritingGraderType } from '../../../../types/check-writing.types.ts';
import { WritingRecognitionType } from '../../../../types/sounds-writing.types.ts';

import { getSoundsWritingGraderUtil } from '../../../graders/sounds-writing.grader.ts';
import { getLetterDrawingUtil } from '../../../letter-drawing.utils.ts';
import { getSvgUtil } from '../../../svg.utils.ts';

import { BaseWritingStateChangeHandlerUtil } from './base-writing.handler.ts';

/**
 * Handler for user writing interaction steps
 * Used in SoundsWriting tasks for writing practice and evaluation
 */
export class UserWriteStateChangeHandlerUtil extends BaseWritingStateChangeHandlerUtil {
  private static _instance: UserWriteStateChangeHandlerUtil;
  private isCorrectingLetterWriting = false;
  private lastRecognizedText: string | null = null;
  private readonly writingGraderUtil = getSoundsWritingGraderUtil();

  private constructor() {
    super(getSvgUtil(), getLetterDrawingUtil());
  }

  static get instance(): UserWriteStateChangeHandlerUtil {
    if (!UserWriteStateChangeHandlerUtil._instance) {
      UserWriteStateChangeHandlerUtil._instance = new UserWriteStateChangeHandlerUtil();
    }
    return UserWriteStateChangeHandlerUtil._instance;
  }

  protected getHandlerName(): string {
    return 'UserWrite';
  }

  protected override async getNextStateAfterReadyToStart(): Promise<AppStateEnum> {
    return Promise.resolve(AppStateEnum.ReceivingDrawing);
  }

  protected override async onReceivingDrawing(): Promise<void> {
    console.log(`${this.getHandlerName()} onReceivingDrawing`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Hide ball during drawing
    dispatch(setBallIsVisible(false));

    // Enable drawing for user input
    dispatch(enableDrawing());
  }

  protected override async onEvaluatingDrawing(): Promise<void> {
    console.log(`${this.getHandlerName()} onEvaluatingDrawing`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Disable drawing while evaluating
    dispatch(disableDrawing());

    const state = await this.reduxStoreUtil.getRootState();
    const { step } = selectCurrentLessonTaskStep(state);
    const writingPath = state.soundsWriting.userWritingPath;

    // Validate step type
    if (step?.stepType !== StepType.UserWrite) {
      this.handleError(new Error(SoundsWritingErrors.INVALID_STEP_TYPE));
      return;
    }

    // Get prepared path and letter
    const pathData = await this.prepareWritingPath();
    if (!pathData) return;

    const { letter } = pathData;
    dispatch(setBallIsVisible(true));

    try {
      // Convert user's writing actions to SVG path
      const svgPath = this.svgUtil.writingActionsToPath(writingPath);

      // Grade the writing
      const result = await this.writingGraderUtil.grade({
        userPath: svgPath,
        referencePath: pathData.path,
        reference: letter,
        type: CheckWritingGraderType.LETTER,
      });

      // Process evaluation results
      switch (result.evaluation) {
        case CheckWritingEvaluation.CORRECT:
          this.lastRecognizedText = this.getRecognizedText(WritingRecognitionType.CORRECT, letter);
          await dispatch(updateAppStateThunk({ state: AppStateEnum.Correct, blockAnimation: true })).unwrap();
          break;

        case CheckWritingEvaluation.REVERSE:
          this.isCorrectingLetterWriting = true;
          this.lastRecognizedText = this.getRecognizedText(WritingRecognitionType.REVERSE, letter);

          // Set script replacement for feedback
          dispatch(
            setScriptReplacement({
              key: ScriptReplacementKey.ReversedLetter,
              value: letter,
            })
          );

          await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect, blockAnimation: true })).unwrap();
          break;

        case CheckWritingEvaluation.INCORRECT:
          this.isCorrectingLetterWriting = true;
          this.lastRecognizedText = this.getRecognizedText(WritingRecognitionType.INCORRECT, letter);
          await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect, blockAnimation: true })).unwrap();
          break;

        default:
          this.isCorrectingLetterWriting = true;
          this.lastRecognizedText = this.getRecognizedText(WritingRecognitionType.UNKNOWN, letter);
          await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect, blockAnimation: true })).unwrap();
      }
    } catch (error) {
      this.handleError(error as Error);
      this.isCorrectingLetterWriting = true;
      this.lastRecognizedText = this.getRecognizedText(WritingRecognitionType.UNKNOWN, letter);
      await dispatch(updateAppStateThunk({ state: AppStateEnum.Incorrect })).unwrap();
    } finally {
      dispatch(setBallIsVisible(false));
    }
  }

  protected override async onWriting(): Promise<void> {
    console.log(`${this.getHandlerName()} onWriting`);
    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Hide ball during demonstration
    dispatch(setBallIsVisible(false));

    // Animate letter drawing
    await this.animateLetterDrawing();

    // Transition to retry for another attempt
    await dispatch(updateAppStateThunk({ state: AppStateEnum.Retry, blockAnimation: true })).unwrap();
  }

  protected override async onCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onCorrect`);
    await super.handleCorrect({
      assuming: false,
      answer: this.lastRecognizedText ?? SOUNDS_WRITING_RECOGNITION_MESSAGES.NOT_FOUND,
    });
  }

  protected override async onAssumeCorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onAssumeCorrect`);
    await super.handleCorrect({
      assuming: true,
      answer: this.lastRecognizedText ?? SOUNDS_WRITING_RECOGNITION_MESSAGES.NOT_FOUND,
    });
  }

  protected override async onIncorrect(): Promise<void> {
    console.log(`${this.getHandlerName()} onIncorrect`);

    // Determine next state based on context
    const nextState = this.isCorrectingLetterWriting ? AppStateEnum.Writing : undefined;

    await super.handleIncorrect(this.lastRecognizedText ?? SOUNDS_WRITING_RECOGNITION_MESSAGES.NOT_FOUND, nextState);
  }

  protected override async onRetry(): Promise<void> {
    console.log(`${this.getHandlerName()} onRetry`);
    await super.onRetry();

    const dispatch = await this.reduxStoreUtil.getDispatch();

    // Clear writing area
    await this.clearWritingArea();

    // Speak retry instructions
    await this.audioPlayerUtil.speakStep();
    await this.audioPlayerUtil.waitSpeakCompletion();

    // Transition back to receiving drawing for another attempt
    await dispatch(updateAppStateThunk({ state: AppStateEnum.ReceivingDrawing, blockAnimation: true })).unwrap();
  }

  private getRecognizedText(type: WritingRecognitionType, letter: string): string | null {
    switch (type) {
      case WritingRecognitionType.CORRECT:
        return SOUNDS_WRITING_RECOGNITION_MESSAGES.CORRECT(letter);
      case WritingRecognitionType.INCORRECT:
        return SOUNDS_WRITING_RECOGNITION_MESSAGES.INCORRECT(letter);
      case WritingRecognitionType.REVERSE:
        return SOUNDS_WRITING_RECOGNITION_MESSAGES.REVERSE(letter);
      case WritingRecognitionType.UNKNOWN:
        return SOUNDS_WRITING_RECOGNITION_MESSAGES.UNKNOWN(letter);
      case WritingRecognitionType.NOT_FOUND:
        return SOUNDS_WRITING_RECOGNITION_MESSAGES.NOT_FOUND;
      default:
        return null;
    }
  }
}

export const getUserWriteStateChangeHandlerUtil = (): UserWriteStateChangeHandlerUtil =>
  UserWriteStateChangeHandlerUtil.instance;
